import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { adminApi, formatApiError } from "../auth";
import { UploadCloud, X } from "lucide-react";

const CATS = ["Summer", "Winter", "All-Season"];
const GRADES = ["A", "B", "C", "D", "E", "F", "G"];

const EMPTY = {
  brand: "", model: "", size: "", load_index: 91, speed_rating: "W",
  fuel_efficiency: "B", wet_grip: "B", noise_rating: 68,
  price: 0, sale_price: null, wholesale_cost: null, stock_qty: 0,
  description: "", category: "Summer", images: [], is_visible: true,
};

const AdminProductForm = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [uploadingCount, setUploadingCount] = useState(0);
  const fileRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isEdit) return;
    adminApi.get(`/products/${id}`)
      .then((r) => setForm({ ...EMPTY, ...r.data }))
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onUpload = async (files) => {
    const arr = Array.from(files).slice(0, 5 - form.images.length);
    if (arr.length === 0) return;
    setUploadingCount(arr.length);
    setErr("");
    try {
      const urls = [];
      for (const f of arr) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await adminApi.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      setForm((s) => ({ ...s, images: [...s.images, ...urls].slice(0, 5) }));
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setUploadingCount(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeImage = (i) => setForm((s) => ({ ...s, images: s.images.filter((_, idx) => idx !== i) }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.images.length === 0) { setErr("At least one image is required."); return; }
    setSaving(true);
    const payload = {
      brand: form.brand, model: form.model, size: form.size,
      load_index: Number(form.load_index), speed_rating: form.speed_rating,
      fuel_efficiency: form.fuel_efficiency, wet_grip: form.wet_grip,
      noise_rating: Number(form.noise_rating),
      price: Number(form.price),
      sale_price: form.sale_price === null || form.sale_price === "" ? null : Number(form.sale_price),
      wholesale_cost: form.wholesale_cost === null || form.wholesale_cost === "" ? null : Number(form.wholesale_cost),
      stock_qty: Number(form.stock_qty),
      description: form.description,
      category: form.category,
      images: form.images,
      is_visible: !!form.is_visible,
    };
    try {
      if (isEdit) await adminApi.put(`/products/${id}`, payload);
      else await adminApi.post("/products", payload);
      navigate("/admin/products");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="vd-muted">Loading…</p>;

  const margin = (form.price > 0 && form.wholesale_cost > 0)
    ? (((Number(form.price) - Number(form.wholesale_cost)) / Number(form.price)) * 100).toFixed(1)
    : null;

  return (
    <form onSubmit={onSubmit} data-testid="admin-product-form">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 style={{ fontSize: 28 }}>{isEdit ? "Edit Product" : "New Product"}</h1>
          <p className="vd-muted">{isEdit ? "Update tyre details — changes go live immediately if Visible." : "Add a new tyre to the catalogue."}</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => navigate("/admin/products")} className="vd-btn-secondary" data-testid="product-form-cancel">Cancel</button>
          <button type="submit" disabled={saving} className="vd-btn-primary" data-testid="product-form-save">{saving ? "Saving…" : (isEdit ? "Save changes" : "Create product")}</button>
        </div>
      </div>

      {err && <p className="vd-card p-3 mb-4 text-sm" style={{ color: "var(--primary-red)" }} data-testid="product-form-error">{err}</p>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 flex flex-col gap-5">
          <Section title="Identity">
            <Field label="Brand" required><input className="vd-input" required value={form.brand} onChange={(e) => set("brand", e.target.value)} data-testid="pf-brand" /></Field>
            <Field label="Model" required><input className="vd-input" required value={form.model} onChange={(e) => set("model", e.target.value)} data-testid="pf-model" /></Field>
            <Field label="Size (e.g. 205/55 R16)" required><input className="vd-input" required placeholder="205/55 R16" value={form.size} onChange={(e) => set("size", e.target.value)} data-testid="pf-size" /></Field>
            <Field label="Category" required>
              <select className="vd-select" value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="pf-category">
                {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </Section>

          <Section title="Specifications">
            <Field label="Load Index" required><input type="number" min="50" max="200" className="vd-input" required value={form.load_index} onChange={(e) => set("load_index", e.target.value)} data-testid="pf-load-index" /></Field>
            <Field label="Speed Rating" required><input className="vd-input" required value={form.speed_rating} onChange={(e) => set("speed_rating", e.target.value)} data-testid="pf-speed" /></Field>
            <Field label="Fuel Efficiency (A–G)" required>
              <select className="vd-select" value={form.fuel_efficiency} onChange={(e) => set("fuel_efficiency", e.target.value)} data-testid="pf-fuel">{GRADES.map((g) => <option key={g} value={g}>{g}</option>)}</select>
            </Field>
            <Field label="Wet Grip (A–G)" required>
              <select className="vd-select" value={form.wet_grip} onChange={(e) => set("wet_grip", e.target.value)} data-testid="pf-wet">{GRADES.map((g) => <option key={g} value={g}>{g}</option>)}</select>
            </Field>
            <Field label="Noise Rating (dB)" required><input type="number" min="50" max="90" className="vd-input" required value={form.noise_rating} onChange={(e) => set("noise_rating", e.target.value)} data-testid="pf-noise" /></Field>
          </Section>

          <Section title="Pricing & Stock">
            <Field label="Price (£)" required><input type="number" step="0.01" min="0" className="vd-input" required value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="pf-price" /></Field>
            <Field label="Sale Price (£) — optional"><input type="number" step="0.01" min="0" className="vd-input" value={form.sale_price ?? ""} onChange={(e) => set("sale_price", e.target.value || null)} data-testid="pf-sale-price" /></Field>
            <Field label="Wholesale Cost (£) — optional"><input type="number" step="0.01" min="0" className="vd-input" value={form.wholesale_cost ?? ""} onChange={(e) => set("wholesale_cost", e.target.value || null)} data-testid="pf-wholesale" /></Field>
            <Field label="Stock Quantity" required><input type="number" min="0" className="vd-input" required value={form.stock_qty} onChange={(e) => set("stock_qty", e.target.value)} data-testid="pf-stock" /></Field>
            {margin && (
              <div className="col-span-2 vd-card p-3 text-sm" data-testid="pf-margin">
                <span className="vd-muted">Profit margin: </span>
                <span className="vd-heading font-bold" style={{ color: margin >= 0 ? "#00A35C" : "var(--primary-red)" }}>{margin}%</span>
                <span className="vd-muted ml-2">(£{(Number(form.price) - Number(form.wholesale_cost)).toFixed(2)} per unit)</span>
              </div>
            )}
          </Section>

          <Section title="Description">
            <div className="col-span-2">
              <textarea rows={5} className="vd-input" placeholder="Product description (visible on PDP)" value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="pf-description" />
            </div>
          </Section>
        </div>

        <div className="flex flex-col gap-5">
          <Section title="Images (1–5 required)">
            <div className="col-span-2">
              <div className="grid grid-cols-3 gap-2 mb-3" data-testid="pf-image-grid">
                {form.images.map((src, i) => (
                  <div key={i} className="relative aspect-square rounded-md overflow-hidden" style={{ border: "1px solid var(--border-color)" }} data-testid={`pf-image-${i}`}>
                    <img src={src} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 rounded-full p-1" style={{ background: "rgba(0,0,0,0.6)" }} data-testid={`pf-image-remove-${i}`}>
                      <X size={12} color="#fff" />
                    </button>
                  </div>
                ))}
                {form.images.length < 5 && (
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="aspect-square rounded-md flex flex-col items-center justify-center vd-muted text-xs"
                    style={{ border: "1px dashed var(--border-color)", background: "var(--bg-secondary)" }}
                    data-testid="pf-image-add"
                  >
                    <UploadCloud size={20} />
                    <span className="mt-1">Add</span>
                  </button>
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                hidden
                onChange={(e) => onUpload(e.target.files)}
                data-testid="pf-file-input"
              />
              <p className="vd-muted text-xs">JPG / PNG / WEBP — max 5 MB each. Auto-compressed to &lt; 500 KB.</p>
              {uploadingCount > 0 && <p className="vd-muted text-xs mt-1">Uploading {uploadingCount} image(s)…</p>}
            </div>
          </Section>

          <Section title="Visibility">
            <label className="col-span-2 flex items-center gap-3 cursor-pointer">
              <input type="checkbox" className="vd-checkbox" checked={form.is_visible} onChange={(e) => set("is_visible", e.target.checked)} data-testid="pf-visible" />
              <span className="vd-body text-sm">Show on public site</span>
            </label>
          </Section>
        </div>
      </div>
    </form>
  );
};

const Section = ({ title, children }) => (
  <div className="vd-card p-5">
    <p className="vd-heading font-semibold mb-4">{title}</p>
    <div className="grid grid-cols-2 gap-3">{children}</div>
  </div>
);

const Field = ({ label, children, required }) => (
  <label className="flex flex-col gap-1">
    <span className="vd-muted text-xs uppercase tracking-wider">{label}{required && <span style={{ color: "var(--primary-red)" }}> *</span>}</span>
    {children}
  </label>
);

export default AdminProductForm;
